import React from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

// Silent error reporter - sends to backend for admin monitoring
function reportErrorToBackend(error, errorInfo, errorType = 'boundary') {
  try {
    const userRaw = localStorage.getItem('user');
    let userData = {};
    try { userData = userRaw ? JSON.parse(userRaw) : {}; } catch {}

    // Detect browser
    const ua = navigator.userAgent || '';
    let browser = 'Unknown';
    if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
    else if (ua.includes('Edg')) browser = 'Edge';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';

    fetch(`${API}/api/errors/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: error?.message || String(error),
        stack: error?.stack || '',
        component_stack: errorInfo?.componentStack || '',
        page_url: window.location.href,
        browser,
        user_agent: ua,
        user_id: userData.id || null,
        user_role: userData.role || null,
        user_name: userData.first_name ? `${userData.first_name} ${userData.last_name || ''}`.trim() : null,
        error_type: errorType,
      }),
    }).catch(() => {}); // Never fail silently
  } catch {
    // Never crash on error reporting
  }
}

// Export for use in index.js
window.__reportErrorToBackend = reportErrorToBackend;

// Known benign errors caused by Chrome extensions or Chrome Translate
// modifying DOM nodes that React manages
const BENIGN_ERROR_PATTERNS = [
  "Failed to execute 'removeChild' on 'Node'",
  "Failed to execute 'insertBefore' on 'Node'",
  "Failed to execute 'replaceChild' on 'Node'",
  "Failed to execute 'appendChild' on 'Node'",
  "The node to be removed is not a child of this node",
  "NotFoundError",
  "Node was not found",
  "Cannot read properties of null (reading 'removeChild')",
  "removeChild",
  "insertBefore",
  // Chrome extension injection errors
  "Script error",
  "Extension context invalidated",
  // Chunk loading errors
  "Loading chunk",
  "ChunkLoadError",
  // ResizeObserver
  "ResizeObserver loop",
];

function isBenignError(error) {
  if (!error) return false;
  const msg = (error.message || error.toString() || '').toLowerCase();
  return BENIGN_ERROR_PATTERNS.some(pattern => msg.toLowerCase().includes(pattern.toLowerCase()));
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, retryCount: 0 };
    this.maxRetries = 3;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log for debugging
    console.error('ErrorBoundary caught:', error?.message || error);

    // Report to backend for admin monitoring
    reportErrorToBackend(error, errorInfo, 'boundary');

    // If this is a benign error (Chrome extension/translate interference),
    // auto-recover without showing the error screen
    if (isBenignError(error) && this.state.retryCount < this.maxRetries) {
      console.warn('ErrorBoundary: benign error detected, auto-recovering (attempt', this.state.retryCount + 1, '/', this.maxRetries, ')');
      setTimeout(() => {
        this.setState(prev => ({
          hasError: false,
          error: null,
          retryCount: prev.retryCount + 1
        }));
      }, 100);
      return;
    }

    // For non-benign errors, also try auto-recovery on first occurrence
    if (this.state.retryCount < 1) {
      console.warn('ErrorBoundary: attempting auto-recovery for non-benign error');
      setTimeout(() => {
        this.setState({ hasError: false, error: null, retryCount: 1 });
      }, 200);
      return;
    }
  }

  componentDidUpdate(prevProps) {
    // Reset error state when route changes (navigation)
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null, retryCount: 0 });
    }
  }

  clearCachesAndReload = async () => {
    try {
      // 1. Clear all service worker caches
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.map(n => caches.delete(n)));
      }

      // 2. Tell the service worker to clear its caches too
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
      }

      // 3. Force reload bypassing cache
      window.location.reload(true);
    } catch {
      // Fallback: simple reload
      window.location.reload();
    }
  };

  handleReload = () => {
    this.clearCachesAndReload();
  };

  handleGoBack = () => {
    this.setState({ hasError: false, error: null, retryCount: 0 });
    window.history.back();
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, retryCount: 0 });
    window.location.href = '/login';
  };

  handleNuclearReset = async () => {
    try {
      // Unregister all service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(r => r.unregister()));
      }
      // Clear all caches
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.map(n => caches.delete(n)));
      }
      // Clear localStorage except essential items
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');
      localStorage.clear();
      if (token) localStorage.setItem('token', token);
      if (user) localStorage.setItem('user', user);

      // Force reload
      window.location.href = '/login';
    } catch {
      window.location.href = '/login';
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0a0e17',
          color: '#e2e8f0',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}>
          <div style={{ textAlign: 'center', maxWidth: 440, padding: '2rem' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>&#9888;</div>
            <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>
              Une erreur est survenue
            </h2>
            <p style={{ fontSize: 14, color: '#94a3b8', marginBottom: 24, lineHeight: 1.5 }}>
              La page a rencontré un problème. Essayez de vider le cache et recharger.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={this.handleGoBack}
                data-testid="error-boundary-back"
                style={{
                  padding: '10px 24px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#3b82f6',
                  color: 'white',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Retour
              </button>
              <button
                onClick={this.handleReload}
                data-testid="error-boundary-reload"
                style={{
                  padding: '10px 24px',
                  borderRadius: 8,
                  border: '1px solid #334155',
                  background: 'transparent',
                  color: '#94a3b8',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Vider le cache et recharger
              </button>
              <button
                onClick={this.handleGoHome}
                data-testid="error-boundary-home"
                style={{
                  padding: '10px 24px',
                  borderRadius: 8,
                  border: '1px solid #334155',
                  background: 'transparent',
                  color: '#94a3b8',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Accueil
              </button>
            </div>
            <button
              onClick={this.handleNuclearReset}
              data-testid="error-boundary-nuclear-reset"
              style={{
                marginTop: 20,
                padding: '8px 16px',
                borderRadius: 6,
                border: 'none',
                background: 'transparent',
                color: '#64748b',
                fontSize: 12,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Problème persistant ? Réinitialiser complètement
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
